import { createAsyncThunk } from "@reduxjs/toolkit";
import { logout } from "./LoginSlice";
import { logoutUser } from "./AccountLogoutThunk";

/**
 * fetch all Members entries with pagination support
 */
export const fetchMembersItems = createAsyncThunk(
  "members/fetchItems",
  async ({ page = 1,  limit = 20, search = "", reset = true } = {}, thunkAPI) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members?page=${page}&limit=${limit}${searchParam}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Fetch failed");
    }
    return { ...json, reset }; // Include reset flag to determine if we should replace or append data
  }
);
export const fetchMemberById = createAsyncThunk(
  "members/fetchById",
  async (memberId, thunkAPI) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/members/getByParam?_id=${memberId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
      }
    );

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch member");
    }

    return json;
  }
);
/**
 * Load more members for infinite scroll
 */
export const loadMoreMembersItems = createAsyncThunk(
  "members/loadMore",
  async ({ page, search = "" }, thunkAPI) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members?page=${page}&limit=20${searchParam}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Load more failed");
    }
    return json;
  }
);

export const addMembersItem = createAsyncThunk(
  "members/addItem",
  async (data, thunkAPI) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-user-role": "admin",
        },
        body: JSON.stringify(data),
      });

      if (res.status === 401) {
        thunkAPI.dispatch(logout());
        thunkAPI.dispatch(logoutUser());
        window.location.href = "/";
        return thunkAPI.rejectWithValue("Unauthorized");
      }

      const json = await res.json();

      if (!res.ok) {
        // ✅ Reject with value instead of throwing
        return thunkAPI.rejectWithValue(json?.message || "Add failed");
      }

      return json;
    } catch (err) {
      // ✅ Network or unexpected error
      return thunkAPI.rejectWithValue(err.message || "Network error");
    }
  }
);


export const updateMembersItem = createAsyncThunk(
  "members/updateItems",
  async (data, thunkAPI) => {
    const body = JSON.stringify(data)
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members?_id=${data._id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
      body: body
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Update failed");
    }
    return json;
  }
);

export const addAccountHistoryItem = createAsyncThunk(
  "account/historyItem",
  async (data, thunkAPI) => {
    const body = JSON.stringify(data)
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/accountactivityhistory`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
      body: body
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Add history failed");
    }
    return json;
  }
);

export const sendMemberEmail = createAsyncThunk(
  "members/sendEmail", 
  async (data, thunkAPI) => {
    const body = JSON.stringify(data)
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members/resend-password-setup-email`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
      body: body
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Send email failed");
    }
    return json;
  }
);

export const fetchAccountHistoryItem = createAsyncThunk(
  "account/fetchHistoryItem",
  async (_id, thunkAPI) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/accountactivityhistory?account_id=${_id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin"
      },
    });
    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Fetch history failed");
    }
    return json;
  }
);

// GET /members/stripe - Fetch logged-in member's Stripe account
export const fetchStripeAccount = createAsyncThunk(
  "stripe/fetchAccount",
  async (memberId, thunkAPI) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/members/stripe?member_id=${memberId}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-user-role": "admin",
      },
    });

    if (res.status === 401) {
      thunkAPI.dispatch(logout());
      thunkAPI.dispatch(logoutUser());
      window.location.href = "/";
      return thunkAPI.rejectWithValue("Unauthorized");
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || "Failed to fetch Stripe account");
    }
    return json;
  }
);